import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { Google, Meta, Linkedin, Tiktok, Twitter } from '../assets';
import { UserContext } from '../context/UserContext';
import { CountdownTimer } from '../components';
import { apiPost } from '../utils/api';
import { useNotify } from '../components/NotificationComponent';

const FeaturePage = () => {
  const features = [
    {
      title: "Unified Ad Dashboard",
      desc: "Manage Meta, Google, LinkedIn, TikTok campaigns from one powerful, easy-to-use dashboard. No more switching tabs endlessly.",
      emoji: "📊",
    },
    {
      title: "AI-Powered Optimization",
      desc: "Get intelligent ad suggestions based on behavioral psychology, audience insights, and ad platform data in real-time.",
      emoji: "🤖",
    },
    {
      title: "Real-Time Alerts",
      desc: "Receive instant alerts if your ads are underperforming, and get actionable tips to fix them on the spot.",
      emoji: "⚡",
    },
    {
      title: "Behavior Analytics",
      desc: "Understand what your audience *really* wants with deep behavioral analytics — beyond just numbers.",
      emoji: "🧠",
    },
    {
      title: "Budget Efficiency Tools",
      desc: "AI helps reallocate your ad spend where it performs best, saving you wasted budget.",
      emoji: "💸",
    },
    {
      title: "Secure & Compliant",
      desc: "We follow industry best practices in OAuth and never store sensitive access tokens.",
      emoji: "🔒",
    }
  ];

  const integrations = [
    { name: 'Google Ads', logo: Google },
    { name: 'Meta (Facebook)', logo: Meta },
    { name: 'LinkedIn Ads', logo: Linkedin },
    { name: 'TikTok Ads', logo: Tiktok },
    { name: 'Twitter Ads', logo: Twitter }
  ];

  const {userMetaInfo} = useContext(UserContext)
  const { notify } = useNotify()
  
  const handleTrialClick = () => {
    apiPost('start-trial/', {}).then(res => {
      notify('trial has been started for 7 days.');
    }).catch(err => {
      notify(err.response && err.response?.data?.detail ? err.response?.data.detail : "Failed to start trial");
    })
  }

  return (
    <div className="min-h-screen bg-gray-50  font-sans py-24 px-5">
      
      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-center max-w-4xl mx-auto mb-20"
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">All Your Ads. One Smart Dashboard.</h1>
        <p className="text-lg text-gray-600">Connect, Analyze, and Optimize Ads across every major platform — Powered by AI and Behavioral Psychology.</p>
      </motion.section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {features.map((feature, index) => (
          <motion.div 
            key={index}
            whileHover={{ scale: 1.05 }}
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition"
          >
            <div className="text-4xl">{feature.emoji}</div>
            <h3 className="text-xl font-semibold text-gray-800 mt-4">{feature.title}</h3>
            <p className="text-gray-600 mt-2">{feature.desc}</p>
          </motion.div>
        ))}
      </section>

      {/* Integrations */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="py-20"
      >
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">Connect Seamlessly With</h2>
        <div className="flex flex-wrap justify-center items-center gap-10">
          {integrations.map((item, idx) => (
            <div key={idx} className="flex flex-col items-center gap-2">
              <img src={item.logo} alt={item.name} className="w-16 h-16" />
              <span className="text-gray-700">{item.name}</span>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Call to Action */}
      <motion.section
        whileInView={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ delay: 0.2 }}
        className="text-center mt-20"
      >
        <h2 className="text-4xl font-bold text-gray-800 mb-6">Ready to Boost Your Ad Performance?</h2>
        <p className="text-lg text-gray-600 mb-8">Start your 7-day free trial and see the results for yourself.</p>
        {userMetaInfo?.trial ?
          <CountdownTimer endTime={userMetaInfo?.trial?.trial_ends_at} />
        :
          <button onClick={()=>handleTrialClick()} className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-8 py-3 rounded-full text-lg font-semibold hover:opacity-90 transition">
            Try Free for 7 Days
          </button>
        }
      </motion.section>

    </div>
  );
};

export default FeaturePage;
